import { useEffect, useRef, useState } from 'react';
import WheelTimePicker from './WheelTimePicker';
import { verifyContactRequest, submitContactRequest } from '../api/contactRequests';
import { buildInternationalPhone } from '../utils/phone';

const COUNTRY_CODE_OPTIONS = [
  { value: '+972', label: '🇮🇱 ישראל (+972)' },
  { value: '+1', label: '🇺🇸 ארה"ב (+1)' },
  { value: '+44', label: '🇬🇧 בריטניה (+44)' },
  { value: '+49', label: '🇩🇪 גרמניה (+49)' },
  { value: '+33', label: '🇫🇷 צרפת (+33)' },
  { value: '+39', label: '🇮🇹 איטליה (+39)' },
  { value: '+34', label: '🇪🇸 ספרד (+34)' },
  { value: '+31', label: '🇳🇱 הולנד (+31)' },
  { value: '+41', label: '🇨🇭 שוויץ (+41)' },
  { value: '+43', label: '🇦🇹 אוסטריה (+43)' },
  { value: '+32', label: '🇧🇪 בלגיה (+32)' },
  { value: '+46', label: '🇸🇪 שוודיה (+46)' },
  { value: '+47', label: '🇳🇴 נורווגיה (+47)' },
  { value: '+45', label: '🇩🇰 דנמרק (+45)' },
  { value: '+358', label: '🇫🇮 פינלנד (+358)' },
  { value: '+7', label: '🇷🇺 רוסיה (+7)' },
  { value: '+86', label: '🇨🇳 סין (+86)' },
  { value: '+81', label: '🇯🇵 יפן (+81)' },
  { value: '+82', label: '🇰🇷 דרום קוריאה (+82)' },
  { value: '+91', label: '🇮🇳 הודו (+91)' },
  { value: '+61', label: '🇦🇺 אוסטרליה (+61)' },
  { value: '+64', label: '🇳🇿 ניו זילנד (+64)' },
  { value: '+27', label: '🇿🇦 דרום אפריקה (+27)' },
  { value: '+971', label: '🇦🇪 איחוד האמירויות (+971)' },
  { value: '+966', label: '🇸🇦 ערב הסעודית (+966)' },
  { value: '+20', label: '🇪🇬 מצרים (+20)' },
  { value: '+90', label: '🇹🇷 טורקיה (+90)' },
  { value: '+351', label: '🇵🇹 פורטוגל (+351)' },
  { value: '+353', label: '🇮🇪 אירלנד (+353)' },
  { value: '+48', label: '🇵🇱 פולין (+48)' },
  { value: '+420', label: '🇨🇿 צ\'כיה (+420)' },
  { value: '+36', label: '🇭🇺 הונגריה (+36)' },
  { value: '+40', label: '🇷🇴 רומניה (+40)' },
  { value: '+30', label: '🇬🇷 יוון (+30)' },
];

const EMPTY_CONTACT = {
  fullName: '',
  company: '',
  contactMethod: 'phone',
  phone: '',
  countryCode: '+972',
  email: '',
  availableTimes: [{ date: '', fromTime: '', toTime: '' }],
};

function ContactForms() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [successPopup, setSuccessPopup] = useState(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const codeInputRefs = useRef([]);

  useEffect(() => {
    if (showVerificationScreen && codeInputRefs.current[0]) {
      const timer = window.setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [showVerificationScreen]);

  const handleContactChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const clearVerificationState = () => {
    setShowVerificationScreen(false);
    setVerificationCode('');
    setEnteredCode('');
  };

  const addTimeSlot = () => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: [...prev.availableTimes, { date: '', fromTime: '', toTime: '' }],
    }));
  };

  const updateTimeSlot = (index, field, value) => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.map((slot, i) => {
        if (i !== index) return slot;
        const next = { ...slot, [field]: value };
        if (field === 'fromTime' && next.toTime && value && next.toTime < value) {
          next.toTime = '';
        }
        return next;
      }),
    }));
  };

  const removeTimeSlot = (index) => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.filter((_, i) => i !== index),
    }));
  };

  const openSuccess = (name, purpose) => {
    setSuccessPopup({
      name: String(name || '').trim() || 'אורח/ת',
      purpose,
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const name = contactForm.fullName.trim();
    if (!name) {
      alert('יש למלא שם מלא');
      return;
    }
    if (contactForm.contactMethod === 'phone' && !contactForm.phone.trim()) {
      alert('יש למלא מספר טלפון');
      return;
    }
    if (contactForm.contactMethod === 'email' && !contactForm.email.trim()) {
      alert('יש למלא כתובת מייל');
      return;
    }
    const hasTime = (contactForm.availableTimes || []).some(
      (slot) => slot.date && slot.fromTime && slot.toTime
    );
    if (!hasTime) {
      alert('יש לבחור לפחות זמן פנוי אחד');
      return;
    }

    setIsSubmitting(true);
    try {
      const phone =
        contactForm.contactMethod === 'phone'
          ? buildInternationalPhone(contactForm.countryCode, contactForm.phone)
          : undefined;
      const email =
        contactForm.contactMethod === 'email'
          ? contactForm.email.trim()
          : undefined;

      const verifyResult = await verifyContactRequest({
        contactMethod: contactForm.contactMethod,
        preferredContactMethod: contactForm.contactMethod,
        phone,
        email,
      });

      if (verifyResult.success) {
        setVerificationCode(verifyResult.data.verificationCode);
        setShowVerificationScreen(true);
      } else {
        throw new Error(verifyResult.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch (err) {
      alert(`❌ ${err.message || 'אופס! משהו השתבש בשליחת קוד האימות. אנא נסה שנית.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (isVerifying) return;

    setIsVerifying(true);
    try {
      if (enteredCode.trim() !== verificationCode) {
        alert('❌ קוד האימות שגוי. אנא נסה שנית.');
        setEnteredCode('');
        return;
      }

      await submitContactRequest({
        fullName: contactForm.fullName,
        company: contactForm.company,
        contactMethod: contactForm.contactMethod,
        phone: contactForm.phone,
        countryCode: contactForm.countryCode,
        email: contactForm.email,
        availableTimes: contactForm.availableTimes,
        verificationCode: enteredCode.trim(),
      });

      const name = contactForm.fullName.trim();
      setContactForm(EMPTY_CONTACT);
      clearVerificationState();
      openSuccess(name, 'talk');
    } catch (err) {
      alert(`❌ ${err.message || 'שליחת הטופס נכשלה. נסו שוב.'}`);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <>
      <section className="app-download-section scroll-animate" id="contact">
        <div className="container">
          <div className="contact-section-heading">
            <h2 className="section-title">רוצים לדבר איתנו?</h2>
            <p className="contact-section-subtitle">
              ד״ר משה קטן מזמין אתכם לשיחה קצרה על הקבוצה החדשה שהוא מקים.
              בלי התחייבות — רק לבדוק אם זה המקום הנכון עבורכם.
            </p>
          </div>

          <div className="contact-inline-form">
              <form onSubmit={handleContactSubmit}>
                <div className="form-group form-group--name">
                  <label htmlFor="contact-fullName">שם מלא</label>
                  <div className="input-shell">
                    <span className="input-shell-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      id="contact-fullName"
                      type="text"
                      value={contactForm.fullName}
                      onChange={(e) => handleContactChange('fullName', e.target.value)}
                      placeholder="איך קוראים לכם?"
                      required
                    />
                  </div>
                </div>

                <div className="form-group form-group--company">
                  <label htmlFor="contact-company">שם החברה</label>
                  <div className="input-shell">
                    <span className="input-shell-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 21h18" />
                        <path d="M5 21V7l7-4 7 4v14" />
                        <path d="M9 21v-4h6v4" />
                      </svg>
                    </span>
                    <input
                      id="contact-company"
                      type="text"
                      value={contactForm.company}
                      onChange={(e) => handleContactChange('company', e.target.value)}
                      placeholder="החברה שאתם מובילים"
                    />
                  </div>
                </div>

                <div className="form-group form-group--method">
                  <label>אמצעי יצירת קשר מועדף</label>
                  <div className="radio-group">
                    <label className={`contact-method-option${contactForm.contactMethod === 'phone' ? ' selected' : ''}`}>
                      <input
                        type="radio"
                        name="contactMethod"
                        value="phone"
                        checked={contactForm.contactMethod === 'phone'}
                        onChange={(e) => handleContactChange('contactMethod', e.target.value)}
                      />
                      <span className="method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </span>
                      <span className="method-text">
                        <span className="method-title">טלפון</span>
                        <span className="method-hint">שיחה או הודעה</span>
                      </span>
                    </label>
                    <label className={`contact-method-option${contactForm.contactMethod === 'email' ? ' selected' : ''}`}>
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={contactForm.contactMethod === 'email'}
                        onChange={(e) => handleContactChange('contactMethod', e.target.value)}
                      />
                      <span className="method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                      <span className="method-text">
                        <span className="method-title">מייל</span>
                        <span className="method-hint">תכתובת נוחה</span>
                      </span>
                    </label>
                  </div>
                </div>

                {contactForm.contactMethod === 'phone' ? (
                  <div className="form-group form-group--contact">
                    <label htmlFor="contact-phone">טלפון</label>
                    <div className="phone-input-container">
                      <div className="input-shell phone-number-shell">
                        <input
                          id="contact-phone"
                          type="tel"
                          value={contactForm.phone}
                          onChange={(e) => handleContactChange('phone', e.target.value)}
                          placeholder="מספר טלפון"
                          required
                        />
                      </div>
                      <select
                        value={contactForm.countryCode}
                        onChange={(e) => handleContactChange('countryCode', e.target.value)}
                        className="country-code-select"
                        aria-label="קידומת מדינה"
                      >
                        {COUNTRY_CODE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="form-group form-group--contact">
                    <label htmlFor="contact-email">מייל</label>
                    <div className="input-shell">
                      <span className="input-shell-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                      <input
                        id="contact-email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => handleContactChange('email', e.target.value)}
                        placeholder="name@example.com"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="form-group form-group--times">
                  <label className="times-main-label">זמנים פנויים</label>
                  {contactForm.availableTimes.map((timeSlot, index) => (
                    <div key={index} className="time-slot-container" style={{ '--slot-i': index }}>
                      <div className="time-slot-header">
                        <h4 className="time-slot-title">
                          <span className="time-slot-badge">{index + 1}</span>
                          זמן פנוי
                        </h4>
                        {contactForm.availableTimes.length > 1 && (
                          <button
                            type="button"
                            className="remove-time"
                            onClick={() => removeTimeSlot(index)}
                            title="הסר זמן"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      <div className="date-picker-section">
                        <label className="time-label">תאריך</label>
                        <input
                          type="date"
                          value={timeSlot.date}
                          onChange={(e) => updateTimeSlot(index, 'date', e.target.value)}
                          className="date-input"
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>

                      <div className="time-pickers-section">
                        <div className="time-picker-group">
                          <WheelTimePicker
                            value={timeSlot.fromTime}
                            onChange={(value) => updateTimeSlot(index, 'fromTime', value)}
                            label="משעה"
                            required
                          />
                        </div>
                        <div className="time-picker-group">
                          <WheelTimePicker
                            value={timeSlot.toTime}
                            onChange={(value) => updateTimeSlot(index, 'toTime', value)}
                            label="עד שעה"
                            disabled={!timeSlot.fromTime}
                            minTime={timeSlot.fromTime}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="add-time-button" onClick={addTimeSlot}>
                    <span className="add-icon">+</span>
                    הוסף זמן נוסף
                  </button>
                </div>

                <button type="submit" className="submit-contact" disabled={isSubmitting}>
                  <span className="submit-contact-label">
                    {isSubmitting ? 'שולח...' : 'לתיאום שיחת היכרות'}
                  </span>
                  {!isSubmitting && (
                    <span className="submit-contact-arrow" aria-hidden="true">
                      ←
                    </span>
                  )}
                </button>
              </form>
          </div>
        </div>
      </section>

      {showVerificationScreen && (
        <div
          className="contact-popup-overlay verification-overlay"
          onClick={clearVerificationState}
        >
          <div className="contact-popup verification-popup" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="close-popup" onClick={clearVerificationState} aria-label="סגירה">
              ✕
            </button>

            <div className="verification-header">
              <div className="verification-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none" />
                  <path d="M8 12l2 2 4-4" stroke="#ffc915" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="verification-title">אימות קשר</h2>
              <p className="verification-subtitle">
                {contactForm.contactMethod === 'email'
                  ? `שלחנו קוד אימות למייל ${contactForm.email}`
                  : `שלחנו קוד אימות למספר ${buildInternationalPhone(contactForm.countryCode, contactForm.phone)}`}
              </p>
            </div>

            <form onSubmit={handleVerificationSubmit} className="verification-form">
              <div className="verification-code-container">
                <label className="verification-label">קוד האימות</label>
                <div className="code-input-wrapper">
                  {[0, 1, 2, 3].map((index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        codeInputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      className={`code-digit${enteredCode[index] ? ' has-value' : ''}`}
                      value={enteredCode[index] || ''}
                      aria-label={`ספרה ${index + 1} מתוך 4`}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                        if (value) {
                          const newCode = enteredCode.split('');
                          newCode[index] = value;
                          const updatedCode = newCode.join('').slice(0, 4);
                          setEnteredCode(updatedCode);
                          if (index < 3 && codeInputRefs.current[index + 1]) {
                            codeInputRefs.current[index + 1].focus();
                          }
                        } else {
                          const newCode = enteredCode.split('');
                          newCode[index] = '';
                          setEnteredCode(newCode.join(''));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !enteredCode[index] && index > 0) {
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowLeft' && index > 0) {
                          e.preventDefault();
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowRight' && index < 3) {
                          e.preventDefault();
                          codeInputRefs.current[index + 1]?.focus();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
                        if (pastedData) {
                          setEnteredCode(pastedData);
                          const nextIndex = Math.min(index + pastedData.length, 3);
                          codeInputRefs.current[nextIndex]?.focus();
                        }
                      }}
                      maxLength="1"
                      required
                    />
                  ))}
                </div>
                <p className="code-hint">הכנס את הקוד בן 4 הספרות שקיבלת</p>
              </div>

              <button
                type="submit"
                className="verify-submit-btn"
                disabled={isVerifying || enteredCode.length !== 4}
              >
                {isVerifying ? 'מאמת...' : 'אמת ושלח'}
              </button>

              <button
                type="button"
                className="back-to-form-btn"
                onClick={clearVerificationState}
              >
                ← חזור לטופס
              </button>
            </form>
          </div>
        </div>
      )}

      {successPopup && (
        <div className="contact-popup-overlay success-popup-overlay" onClick={() => setSuccessPopup(null)}>
          <div
            className="contact-popup success-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="success-popup-title"
          >
            <button
              type="button"
              className="close-popup"
              onClick={() => setSuccessPopup(null)}
              aria-label="סגירה"
            >
              ✕
            </button>

            <div className="success-popup-icon" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none" />
                <path
                  d="M8 12l2 2 4-4"
                  stroke="#383c7f"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h2 id="success-popup-title" className="success-popup-title">
              היי {successPopup.name}!
            </h2>
            <p className="success-popup-message">
              הפרטים התקבלו בהצלחה. ד״ר משה קטן ייצור איתך קשר לתיאום שיחת היכרות קצרה.
            </p>

            <button
              type="button"
              className="success-popup-close-btn"
              onClick={() => setSuccessPopup(null)}
            >
              סגירה
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ContactForms;
